import {
  type MediaTileProps,
  getMediaTileFileIcon,
} from "@icg360/design-system";

import type {
  InspectionAssetSource,
  KeystoneInspectionAsset,
} from "data/keystone-api-mapping";
import { formatDate, localTimeFromTimestamp } from "utils";

/**
 * Newest `lastUpdatedTimestamp` first — matches WriteOutCard thumbnail order.
 * IDs with no matching asset are appended in their original relative order.
 */
export function sortInspectionAssetIdsByLastUpdatedDesc(
  ids: string[],
  assets: KeystoneInspectionAsset[] | undefined,
): string[] {
  if (!ids.length) {
    return [];
  }
  if (!assets?.length) {
    return [...ids];
  }

  const byId = new Map(assets.map((a) => [a.id, a] as const));
  const known: KeystoneInspectionAsset[] = [];
  const unknown: string[] = [];

  for (const id of ids) {
    const a = byId.get(id);
    if (a) {
      known.push(a);
    } else {
      unknown.push(id);
    }
  }

  known.sort(
    (a, b) =>
      new Date(b.lastUpdatedTimestamp).getTime() -
      new Date(a.lastUpdatedTimestamp).getTime(),
  );

  return [...known.map((a) => a.id), ...unknown];
}

/** Aligns with @icg360/design-system MediaTile preview rules (PDF object tag omitted on desktop Safari). */
function isDesktopSafari() {
  return (
    /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent) &&
    !/Mobi|Android|iPad|iPhone|iPod/i.test(navigator.userAgent)
  );
}

const OCTET_STREAM = "application/octet-stream";

function isMeaningfulMime(mime: string): boolean {
  const m = mime.trim().toLowerCase();
  return m.length > 0 && m !== OCTET_STREAM;
}

/**
 * Zero-byte `File` with an `image/*` type so `getMediaTileFileIcon` returns `undefined` and MediaTile
 * shows the raster preview from `source` instead of a document icon.
 */
export function galleryPlaceholderImageFile(
  filename: string,
  imageMimeType: string,
): File {
  return new File([], filename, { type: imageMimeType.toLowerCase() });
}

export type WriteOutThumbnailMediaProps = Pick<
  MediaTileProps,
  "source" | "file" | "icon" | "name"
>;

function isHeicOrHeifMime(mime: string): boolean {
  const m = mime.toLowerCase();
  return m === "image/heic" || m === "image/heif";
}

/**
 * True for raster types safe to show via `<img src>` + API URL (excludes HEIC/HEIF).
 * API sometimes sends the generic top-level type `image` instead of `image/jpeg`, etc.
 */
export function isDirectRasterImageMime(mime: string | undefined): boolean {
  if (!mime) {
    return false;
  }
  const m = mime.trim().toLowerCase();
  if (m === "image") {
    return true;
  }
  return m.startsWith("image/") && !isHeicOrHeifMime(m);
}

/**
 * MIME for the zero-byte placeholder `File` when using a direct image URL. Bare API `image` maps
 * to `image/jpeg` so the `File` has a valid `image/*` subtype.
 */
export function resolveGalleryImageMimeForUrl(mimeFromApi: string): string {
  const api = mimeFromApi.trim().toLowerCase();
  if (api === "image") {
    return "image/jpeg";
  }
  if (isDirectRasterImageMime(api)) {
    return api;
  }
  return "image/jpeg";
}

/**
 * When true, use the inspection image URL with a placeholder `File` for MediaTile; when false,
 * fetch bytes and derive preview (PDF / non-raster). Matches thumbnail behavior.
 */
export function inspectionAssetUsesDirectImageUrl(
  asset: KeystoneInspectionAsset | undefined,
): boolean {
  if (!asset) {
    return true;
  }
  return isDirectRasterImageMime(asset.assetType);
}

/**
 * Props for WriteOutCard / ineligible thumbnails: same rules as the inspection gallery — raster
 * images use the API URL (plus a typed placeholder `File` for design-system quirks); PDFs and
 * other types use a file-type icon instead of a broken `<img>`.
 */
export function writeOutThumbnailMediaTileProps(
  asset: KeystoneInspectionAsset | undefined,
  imageUrl: string,
): WriteOutThumbnailMediaProps {
  if (!asset) {
    return { source: imageUrl };
  }

  const resolvedName = asset.assetFileName ?? `asset_${asset.id}`;
  const mimeFromApi = asset.assetType ?? OCTET_STREAM;

  if (inspectionAssetUsesDirectImageUrl(asset)) {
    return {
      source: imageUrl,
      file: galleryPlaceholderImageFile(
        resolvedName,
        resolveGalleryImageMimeForUrl(mimeFromApi),
      ),
    };
  }

  const apiLower = mimeFromApi.trim().toLowerCase();
  const effectiveMime = isMeaningfulMime(apiLower) ? apiLower : OCTET_STREAM;

  const placeholder = new File([], resolvedName, { type: effectiveMime });
  let icon = getMediaTileFileIcon(placeholder);
  if (icon === undefined) {
    icon =
      effectiveMime === "application/pdf"
        ? "FilePdf"
        : ("Document" as NonNullable<MediaTileProps["icon"]>);
  }

  return {
    source: "",
    icon,
    name: resolvedName,
  };
}

export function blobSourceForInspectionFile(file: File): string {
  const type = file.type.toLowerCase();
  if (type === "image/heic" || type === "image/heif") {
    return "";
  }
  if (type === "image" || type.startsWith("image/")) {
    return URL.createObjectURL(file);
  }
  if (type === "application/pdf" && !isDesktopSafari()) {
    return URL.createObjectURL(file);
  }
  return "";
}

/** `"ineligible"` renders the same description string as `"inspection-tab"`. */
export type InspectionAssetDescriptionMode =
  | "inspection-tab"
  | "proof-tab"
  | "ineligible";

export function buildInspectionAssetDescription(
  selectedAsset: KeystoneInspectionAsset | undefined,
  agencyName: string | null | undefined,
  mode: InspectionAssetDescriptionMode,
): string {
  const latestUploadDate = formatDate(
    selectedAsset?.lastUpdatedTimestamp ?? "",
  );
  const latestUploadTime = localTimeFromTimestamp(
    selectedAsset?.lastUpdatedTimestamp ?? "",
  );
  const assetSourceDisplay: Record<InspectionAssetSource, string> = {
    POLICYHOLDER: selectedAsset?.lastUpdatedByAppUserId ?? "",
    PRODUCER: agencyName ? `a representative at ${agencyName}` : "",
    UNDERWRITER: "SageSure support",
    VENDOR: "",
  };
  const latestUploaderDisplay =
    assetSourceDisplay[selectedAsset?.assetSource as InspectionAssetSource] ??
    "";

  // "inspection-tab" and "ineligible" use the same copy; only "proof-tab" differs.
  const proofTabActive = mode === "proof-tab";
  const segments: string[] = [];
  segments.push(proofTabActive ? "Added" : "Inspection photo added");

  if (latestUploaderDisplay) {
    segments.push(`by ${latestUploaderDisplay}`);
  }

  if (proofTabActive) {
    if (latestUploadDate) {
      segments.push(`on ${latestUploadDate}`);
    }
    if (latestUploadTime) {
      segments.push(`at ${latestUploadTime.toLowerCase()}`);
    }
  } else if (latestUploadDate) {
    segments.push(latestUploadDate);
  }

  return segments.join(" ");
}
